import { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { fetchOnboarding } from "actions/onboarding";
import { PlayCircle } from "@mui/icons-material";
import { Page } from "components/ui";
import Carousel from "react-multi-carousel";
import Button from "@mui/material/Button";
import useTranslate from "hooks/use-translate-language";
import useDocumentTitle from "hooks/use-document-title";
import "react-multi-carousel/lib/styles.css";
import "./onboarding.scss";
import VideoPlayerModal from "./video-player-modal";
import PDFIcon from "assets/images/pdf_icon.png";

export default function Onboarding() {
  const translate = useTranslate();
  useDocumentTitle(translate("resources"));
  const dispatch = useDispatch();
  const carouselRef = useRef(null);
  const buttonRefs = useRef([]);
  const [tiles, setTiles] = useState([]);
  const [logoTile, setLogoTile] = useState(undefined);
  const [isFetching, setIsFetching] = useState(false);
  const [openVideoModal, setOpenVideoModal] = useState(false);
  const [videoPlayerUrl, setVideoPlayerUrl] = useState("");
  const [videoTileTitle, setVideoTileTitle] = useState("");
  const [logoVersion] = useState(Date.now());
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(() =>
    window.visualViewport ? window.visualViewport.width : window.innerWidth
  );

  useEffect(() => {
    setIsFetching(true);
    dispatch(fetchOnboarding())
      .then(data => {
        let tilesToShow = [];
        if (data) {
          data.forEach(element => {
            if (element.IsCompanyLogo) {
              setLogoTile(element);
            } else {
              tilesToShow.push(element);
            }
          });
        }
        setTiles(tilesToShow);
      })
      .finally(() => {
        setIsFetching(false);
      });
  }, [dispatch]);

  // Listen for viewport changes (including zoom)
  useEffect(() => {
    const handleResize = () => {
      const width = window.visualViewport ? window.visualViewport.width : window.innerWidth;
      setViewportWidth(width);
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      return () => window.visualViewport.removeEventListener('resize', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  const getFileType = url => {
    const extension = url.split(".").pop().toLowerCase();
    const pdfExtensions = ["pdf"];
    const videoExtensions = ["mp4"];

    if (pdfExtensions.includes(extension)) {
      return "pdf";
    } else if (videoExtensions.includes(extension)) {
      return "video";
    } else {
      return "unknown";
    }
  };

  const openVideoPlayer = (url, videoTitle) => {
    setVideoTileTitle(videoTitle);
    setVideoPlayerUrl(url);
    setOpenVideoModal(true);
  };

  const closeVideoPlayer = () => {
    setVideoTileTitle("");
    setVideoPlayerUrl("");
    setOpenVideoModal(false);
  };

  const handleSlideChange = (nextSlide) => {
    setCurrentSlideIndex(nextSlide);

    // Focus the first button on the new slide after a short delay to allow rendering
    setTimeout(() => {
      const config = responsive(tiles.length);
      let itemsPerSlide;
      if (viewportWidth >= config.desktop.breakpoint.min) {
        itemsPerSlide = config.desktop.items;
      } else if (viewportWidth > config.tablet.breakpoint.min) {
        itemsPerSlide = config.tablet.items;
      } else {
        itemsPerSlide = config.mobile.items;
      }

      const firstCardIndex = nextSlide * itemsPerSlide;
      if (buttonRefs.current[firstCardIndex]) {
        buttonRefs.current[firstCardIndex].focus();
      }
    }, 10);
  };

  const responsive = items => {
    return {
      desktop: {
        breakpoint: {
          max: 3000,
          min: 1024
        },
        items: items < 3 ? items : 3
      },
      tablet: {
        breakpoint: {
          max: 1024,
          min: 640
        },
        items: items < 2 ? items : 2
      },
      mobile: {
        breakpoint: {
          max: 640,
          min: 0
        },
        items: 1
      }
    };
  };

  const getItemsPerSlide = () => {
    const config = responsive(tiles.length);

    let itemsPerSlide;
    if (viewportWidth >= config.desktop.breakpoint.min) {
      itemsPerSlide = config.desktop.items;
    } else if (viewportWidth > config.tablet.breakpoint.min) {
      itemsPerSlide = config.tablet.items;
    } else {
      itemsPerSlide = config.mobile.items;
    }

    return itemsPerSlide;
  };

  const itemsPerSlide = getItemsPerSlide();
  const totalSlides = Math.ceil(tiles.length / itemsPerSlide);

  const getSlideForCard = (cardIndex) => {
    return Math.floor(cardIndex / itemsPerSlide);
  };

  // Determine if a card is visible on the current slide
  const isCardVisible = (cardIndex) => {
    const cardSlide = getSlideForCard(cardIndex);
    return cardSlide === currentSlideIndex;
  };

  // Handle button focus - navigate to the slide containing the focused card
  const handleButtonFocus = (cardIndex) => {
    const targetSlide = getSlideForCard(cardIndex);
    if (targetSlide !== currentSlideIndex && carouselRef.current) {
      setCurrentSlideIndex(targetSlide)
      carouselRef.current.goToSlide(targetSlide);
    }
  };

  return (
    <Page.Container>
      <VideoPlayerModal
        open={openVideoModal}
        handleClose={closeVideoPlayer}
        url={videoPlayerUrl}
        videoTileTitle={videoTileTitle}
      />
      <Page.Header title={translate("welcome")} goBack="/" />
      <Page.Content
        isFetching={isFetching}
        isEmpty={tiles && tiles?.length === 0}
        emptyMessage={translate("onboardingNotAvailable")}
        styles={{ margin: "-40px -20px 2rem" }}
      >
        <div className="company-logo-section">
          {logoTile && (
            <img
              className="company-logo"
              src={`${logoTile.FileUrl}?t=${logoVersion}`}
              alt={logoTile.Title || "Company logo"}
            />
          )}
        </div>
        <div className="tiles-section">
          {tiles && (
            <div className="carousel-wrapper">
              {/* ARIA live region for announcing slide changes (WCAG 4.1.3) */}
              <div
                className="sr-only"
                role="status"
                aria-live="polite"
                aria-atomic="true"
              >
                {tiles.length > 0 && currentSlideIndex < totalSlides && (
                  `${translate("slide")} ${currentSlideIndex + 1} ${translate("of")} ${totalSlides}`
                )}
              </div>
              <Carousel
                ref={carouselRef}
                responsive={responsive(tiles.length)}
                containerClass="carousel-container"
                itemClass="carousel-item"
                showDots={true}
                renderDotsOutside
                arrows={false}
                draggable={false}
                afterChange={(previousSlide, { currentSlide }) => handleSlideChange(currentSlide)}
              >
                {tiles &&
                  tiles.map((tile, index) => {
                    if (!tile.IsCompanyLogo) {
                      const fileType = getFileType(tile.FileUrl);
                      return (
                        <div key={index} className="card-container">
                          <div className="card-title">{tile.Title}</div>
                          <div className="card-description">
                            {tile.Description}
                          </div>

                          {fileType === "pdf" && (
                            <Button
                              ref={(el) => (buttonRefs.current[index] = el)}
                              variant="text"
                              aria-label={`${tile.Title} - ${translate("downloadFile")}`}
                              onClick={() => {
                                window.open(tile.FileUrl, "_blank");
                              }}
                              onFocus={() => handleButtonFocus(index)}
                              tabIndex={isCardVisible(index) ? 0 : -1}
                              startIcon={
                                <img
                                  src={PDFIcon}
                                  alt=""
                                  className="card-pdf-icon"
                                />
                              }
                            >
                              {translate("downloadFile")}
                            </Button>
                          )}
                          {fileType === "video" && (
                            <Button
                              ref={(el) => (buttonRefs.current[index] = el)}
                              variant="text"
                              aria-label={`${tile.Title} - ${translate("watchVideo")}`}
                              onClick={() => {
                                openVideoPlayer(tile.FileUrl, tile.Title);
                              }}
                              onFocus={() => handleButtonFocus(index)}
                              tabIndex={isCardVisible(index) ? 0 : -1}
                              startIcon={
                                <PlayCircle style={{ fontSize: 40 }} aria-hidden="true" />
                              }
                            >
                              {translate("watchVideo")}
                            </Button>
                          )}
                        </div>
                      );
                    }
                  })}
              </Carousel>
            </div>
          )}
        </div>
      </Page.Content>
    </Page.Container>
  );
}
